import React from 'react'
import Modal from 'components/Modal'
import { ButtonContainer, screenSize } from 'styles/shared'
import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import AlertIcon from 'pages/shared/Alert/Icon'
import { color } from 'styles/colors'
import styled from 'styled-components'

type AlertDescriptionsProps = {
  closeCallback: () => void
  isVisible?: boolean
}

const AlertDescriptionsModal = ({ closeCallback, isVisible = false }: AlertDescriptionsProps) => {
  const alertsData = [
    {
      type: 'EquipmentAlert',
      name: 'Critical Alerts',
      severity: 'critical',
      accentColor: color('criticalAlert'),
      description: 'Critical alerts indicate the presence of a problem in the system that is preventing the system from ' +
        'operating as intended. A critical alert will typically indicate that a system cannot operate in one or more ' +
        'modes and is in immediate need of service.'
    },
    {
      type: 'EquipmentAlert',
      name: 'Major Alerts',
      severity: 'major',
      accentColor: color('majorAlert'),
      description: 'Major alerts indicate that an event has occurred that is currently or temporarily affecting system ' +
        'performance or behavior, typically the HVAC System is still able to run in some capacity and service may be required.'
    },
    {
      type: 'EquipmentAlert',
      name: 'Minor Alerts',
      severity: 'minor',
      accentColor: color('minorAlert'),
      description: 'Minor alerts are either for informational purposes or to indicate a minor problem. Some of these ' +
        'can be expected to occur periodically to just provide information about system behavior; these may also help ' +
        'understand causes for major or critical alerts. Others do indicate a problem that should be addressed when ' +
        'possible, but immediate attention isn’t necessary.'
    },
    {
      type: 'SDAAlert',
      name: 'Critical Smart Diagnostics Alerts (SDAs)',
      severity: 'critical-sda',
      accentColor: color('criticalSdaAlert'),
      description: 'Critical Smart Diagnostics Alerts (SDAs) are based on algorithms that run periodically in the cloud. ' +
        'Critical alerts indicate that a system is not operating properly in one or more modes and is in immediate need of service.'
    },
    {
      type: 'SDAAlert',
      name: 'Major Smart Diagnostics Alerts (SDAs)',
      severity: 'major-sda',
      accentColor: color('majorSdaAlert'),
      description: 'Major Smart Diagnostics Alerts (SDAs) are based on algorithms that run periodically in the cloud and are ' +
        'independent of the alerts triggered within the thermostat and HVAC equipment. Major alerts usually affect system ' +
        'performance or behavior. Typically, the HVAC system is still able to run in some capacity but service may be required.'
    },
    {
      type: 'SDAAlert',
      name: 'Minor Smart Diagnostics Alerts (SDAs)',
      severity: 'minor-sda',
      accentColor: color('minorSdaAlert'),
      description: 'Minor Smart Diagnostics Alerts (SDAs) are based on algorithms that run periodically in the cloud ' +
        'and are independent of the alerts triggered within the thermostat and HVAC equipment. Minor SDAs are either ' +
        'for informational purposes or to indicate a minor problem. Some of these can be expected to occur periodically ' +
        'to just provide information about system behavior; these may also help understand causes for major or critical ' +
        'alerts. Others do indicate a problem that should be addressed when possible, but immediate attention isn’t necessary.'
    }
  ]

  const equipmentAlerts = alertsData.filter(alert => alert.type === 'EquipmentAlert')
  const sdaAlerts = alertsData.filter(alert => alert.type === 'SDAAlert')

  return (
    <Modal
      title={'Alert Descriptions'}
      isDisplayed={isVisible}
      closeCallback={() => closeCallback()}
    >
      <S.Content>
        <S.AlertType>
          <S.ColumnHeader>Equipment Alerts:</S.ColumnHeader>
          {equipmentAlerts.map((alert) => (
            <S.Alert key={alert.name}>
              <AlertIcon severity={alert.severity} size={20} />
              <S.AlertInfo>
                <S.Heading style={{ color: alert.accentColor }}>{alert.name}</S.Heading>
                <S.Description>{alert.description}</S.Description>
              </S.AlertInfo>
            </S.Alert>
          ))}
        </S.AlertType>
        <S.AlertType>
          <S.ColumnHeader>Smart Diagnostics Alerts (SDAs):</S.ColumnHeader>
          {sdaAlerts.map((alert) => (
            <S.Alert key={alert.name}>
              <AlertIcon severity={alert.severity} size={20} />
              <S.AlertInfo>
                <S.Heading style={{ color: alert.accentColor }}>{alert.name}</S.Heading>
                <S.Description>{alert.description}</S.Description>
              </S.AlertInfo>
            </S.Alert>
          ))}
        </S.AlertType>
        <S.AlertEvent>
          <S.ColumnHeader>Consolidated Events</S.ColumnHeader>
          <S.AlertEventDescription>
            Consolidated Events are identified when a series of related alerts indicate a particular problem. Based on the alerts triggered the advanced Diagnostics data processing can indicate what the underlying problem is. <br />For example, when multiple equipment alerts related to Low Faults occur within a specific time period, an Event will indicate that the problem is related to Power Surge/Lightning problems.
          </S.AlertEventDescription>
        </S.AlertEvent>
      </S.Content>
      <ButtonContainer noBorder>
        <Button decoration="positive" text="Close" onPress={closeCallback} />
      </ButtonContainer>
    </Modal>
  )
}

export default AlertDescriptionsModal

const S = {
  ColumnHeader: styled.div`
    margin-bottom: 10px;
    font-size: 14px;
    color: ${color('draftAlert')};
    font-weight: 700;
    line-height: 1;
  `,
  Content: styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: 20px;

    @media ${screenSize.small} {
      display: block;
    }
  `,
  Alert: styled.div`
    display: flex;
  `,
  AlertType: styled.div`
  `,
  Heading: styled.h4`
    margin-top: 8px;
  `,
  AlertInfo: styled.div`
    margin-left: 10px;
  `,
  Description: styled.p`
    margin-top: 5px;
    margin-bottom: 20px;
    font-size: 12px;
    line-height: 16px;
  `,
  AlertEvent: styled.div`
    display:flex;
    flex-direction:column;
    grid-column: 1;
    align-self: start;
    top: -80px;
    position: relative;
    @media ${screenSize.small} {
      grid-column: auto;
      top: 0px;
      position: static;
  }
  `,
  AlertEventDescription: styled.p`
    font-size: 12px;
    line-height: 16px;
    margin-left: 2.7em;
  `
}
